import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, Plus,
  AlertTriangle, CheckCircle2, Download, Users as UsersIcon,
} from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  addWeeks, isSameDay, isSameMonth, format, parseISO, isBefore,
  startOfDay, endOfDay,
} from 'date-fns';
import { listTasks, updateTask, listUsers, calendarIcalUrl, listCalendarEvents } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import TaskDetailDialog from '../../components/crm/TaskDetailDialog';
import KanbanBoard from '../../components/crm/KanbanBoard';

const PRI_COLOR = {
  urgent: 'bg-red-600', high: 'bg-orange-500', medium: 'bg-[#0f5c3d]', low: 'bg-gray-400',
};

const VIEWS = ['agenda', 'month', 'week', 'day', 'kanban'];

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640;

const CalendarPage = () => {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState(() => (isMobile() ? 'agenda' : 'month'));
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null); // task being dragged
  const [openTaskId, setOpenTaskId] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Open a task via ?task=<id> deep link
  useEffect(() => {
    const t = searchParams.get('task');
    if (t) setOpenTaskId(t);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use unified calendar feed which includes tasks + lead follow-ups
      const [ev, u] = await Promise.all([
        listCalendarEvents(assigneeFilter ? { assignee_id: assigneeFilter } : {}).catch(() => ({ data: [] })),
        listUsers().catch(() => ({ data: [] })),
      ]);
      // Convert feed rows into task-shaped items for the existing grid renderer
      const rows = (ev.data || []).map((e) => ({
        id: e.ref_id,
        kind: e.kind,
        title: e.title,
        due_date: e.start,
        start_time: e.start,
        end_time: e.end,
        priority: e.priority || 'medium',
        status: e.status || 'todo',
        assignee_id: e.assignee_id,
        lead_id: e.lead_id,
      }));
      setTasks(rows);
      setUsers(u.data || []);
    } catch { toast({ title: 'Failed to load calendar', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [assigneeFilter]);
  useEffect(() => { load(); }, [load]);

  const userLookup = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === 'day') return [startOfDay(cursor), endOfDay(cursor)];
    if (view === 'week') return [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
    if (view === 'agenda') return [startOfDay(cursor), endOfDay(addDays(cursor, 30))];
    return [startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })];
  }, [cursor, view]);

  const days = useMemo(() => {
    const out = [];
    let d = rangeStart;
    while (d <= rangeEnd) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [rangeStart, rangeEnd]);

  const tasksByDay = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      if (!t.due_date) return;
      const key = String(t.due_date).slice(0, 10);
      (m[key] = m[key] || []).push(t);
    });
    return m;
  }, [tasks]);

  const shift = (delta) => {
    if (view === 'day') setCursor(addDays(cursor, delta));
    else if (view === 'week') setCursor(addWeeks(cursor, delta));
    else if (view === 'agenda') setCursor(addDays(cursor, delta * 7));
    else setCursor(addMonths(cursor, delta));
  };

  const dropOn = async (day) => {
    if (!drag) return;
    // Follow-ups are not draggable-editable from the calendar directly
    if (drag.kind === 'followup') {
      toast({ title: 'Edit this follow-up from the Lead page' });
      setDrag(null);
      return;
    }
    const iso = format(day, 'yyyy-MM-dd') + 'T09:00:00';
    // Optimistic update
    setTasks((prev) => prev.map((t) => t.id === drag.id ? { ...t, due_date: iso } : t));
    try {
      await updateTask(drag.id, { due_date: iso });
      toast({ title: 'Task rescheduled', description: `to ${format(day, 'EEE, d MMM')}` });
    } catch {
      toast({ title: 'Reschedule failed', variant: 'destructive' });
      load();
    }
    setDrag(null);
  };

  const title = view === 'day' ? format(cursor, 'EEEE, d MMM yyyy')
             : view === 'week' ? `Week of ${format(startOfWeek(cursor, { weekStartsOn: 1 }), 'd MMM')}`
             : view === 'agenda' ? `From ${format(cursor, 'd MMM yyyy')}`
             : format(cursor, 'MMMM yyyy');

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Scheduling</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Team calendar
          </h1>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-[#0b1a2e]/60">Tasks + lead follow-ups. Tap a pill to open.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}
            data-testid="cal-assignee-filter"
            className="flex-1 sm:flex-none px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm">
            <option value="">Whole team</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
          </select>
          <a href={calendarIcalUrl(assigneeFilter)} target="_blank" rel="noreferrer"
            data-testid="cal-ical-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm font-medium hover:bg-[#f7fbf8]">
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Subscribe (</span>iCal<span className="hidden sm:inline">)</span>
          </a>
        </div>
      </div>

      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        {/* Toolbar */}
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between gap-2 flex-wrap border-b border-black/5 bg-[#f7fbf8]">
          <div className="flex items-center gap-1.5 min-w-0">
            {view !== 'kanban' ? (
              <>
                <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-1.5 rounded hover:bg-black/5"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setCursor(new Date())} data-testid="cal-today" className="px-2 sm:px-3 py-1 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-white">Today</button>
                <button onClick={() => shift(1)} data-testid="cal-next" className="p-1.5 rounded hover:bg-black/5"><ChevronRight className="w-4 h-4" /></button>
                <div className="ml-1 sm:ml-3 text-sm sm:text-lg font-medium text-[#0b1a2e] truncate">{title}</div>
              </>
            ) : (
              <div className="text-lg font-medium text-[#0b1a2e]">Kanban board</div>
            )}
          </div>
          <div className="flex gap-0.5 rounded-full bg-white border border-[#0f5c3d]/15 p-0.5 sm:p-1 overflow-x-auto max-w-full">
            {VIEWS.map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`px-2 sm:px-3 py-1 text-[11px] sm:text-xs font-medium rounded-full capitalize whitespace-nowrap ${view === v ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-black/5'}`}>{v}</button>
            ))}
          </div>
        </div>

        {view === 'kanban' ? (
          <div className="p-4">
            <KanbanBoard />
          </div>
        ) : loading ? (
          <div className="p-16 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading tasks…</div>
        ) : view === 'agenda' ? (
          <div className="divide-y divide-black/5" data-testid="cal-agenda">
            {days.filter((d) => (tasksByDay[format(d, 'yyyy-MM-dd')] || []).length > 0).length === 0 ? (
              <div className="p-10 text-center text-sm text-[#0b1a2e]/50">
                <CalendarIcon className="w-8 h-8 mx-auto mb-2 text-[#0b1a2e]/20" />
                No tasks scheduled in the next 30 days.
              </div>
            ) : days.map((d) => {
              const key = format(d, 'yyyy-MM-dd');
              const dayTasks = tasksByDay[key] || [];
              if (dayTasks.length === 0) return null;
              const isToday = isSameDay(d, new Date());
              return (
                <div key={key} className="px-3 sm:px-5 py-3">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-widest ${isToday ? 'text-[#0f5c3d]' : 'text-[#0b1a2e]/60'}`}>
                      {isToday ? 'Today · ' : ''}{format(d, 'EEE, d MMM')}
                    </span>
                    <span className="text-[10px] text-[#0b1a2e]/40">· {dayTasks.length} item{dayTasks.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="space-y-1.5">
                    {dayTasks.map((t) => {
                      const overdue = t.status !== 'done' && isBefore(parseISO(t.due_date), startOfDay(new Date()));
                      return (
                        <button key={t.id + (t.kind || '')}
                          data-testid="cal-agenda-item"
                          onClick={() => { if (t.kind !== 'followup') setOpenTaskId(t.id); }}
                          disabled={t.kind === 'followup'}
                          className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm ${t.status === 'done' ? 'opacity-60 line-through bg-[#f7fbf8]' : overdue ? 'bg-red-50' : 'bg-[#f7fbf8] hover:bg-[#eff5f0]'} ${t.kind === 'followup' ? 'italic' : ''}`}>
                          <span className={`w-1.5 h-6 rounded ${PRI_COLOR[t.priority] || PRI_COLOR.medium} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[#0b1a2e] font-medium truncate">{t.title}</div>
                            <div className="text-[11px] text-[#0b1a2e]/50 flex items-center gap-1.5 flex-wrap">
                              {overdue && <span className="text-red-700 font-semibold inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                              {t.assignee_id && userLookup[t.assignee_id]?.name && <span>{userLookup[t.assignee_id].name}</span>}
                              {t.kind === 'followup' && <span className="text-[#0f5c3d]">Lead follow-up</span>}
                            </div>
                          </div>
                          {t.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {/* Weekday header (month + week) */}
            {(view === 'month' || view === 'week') && (
              <div className="grid grid-cols-7 border-b border-black/5">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={i} className="px-1 sm:px-2 py-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 font-semibold text-center bg-[#f7fbf8]/50">
                    <span className="sm:hidden">{d}</span>
                    <span className="hidden sm:inline">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Grid */}
            <div className={`grid ${view === 'day' ? 'grid-cols-1' : 'grid-cols-7'}`}>
              {days.map((d) => {
                const key = format(d, 'yyyy-MM-dd');
                const dayTasks = tasksByDay[key] || [];
                const isCurrentMonth = view === 'month' ? isSameMonth(d, cursor) : true;
                const isToday = isSameDay(d, new Date());
                return (
                  <div
                    key={key}
                    data-testid="cal-day"
                    onDragOver={(e) => { if (drag) e.preventDefault(); }}
                    onDrop={() => dropOn(d)}
                    className={`min-h-[70px] sm:min-h-[110px] p-1 sm:p-1.5 border-r border-b border-black/5 ${isCurrentMonth ? 'bg-white' : 'bg-[#f7fbf8]/40'} ${drag ? 'hover:bg-emerald-50' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[11px] sm:text-xs font-semibold ${isToday ? 'bg-[#0f5c3d] text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 inline-flex items-center justify-center' : isCurrentMonth ? 'text-[#0b1a2e]' : 'text-[#0b1a2e]/30'}`}>
                        {format(d, 'd')}
                      </span>
                      {view === 'day' && <span className="text-xs text-[#0b1a2e]/50">{format(d, 'EEEE')}</span>}
                    </div>
                    <div className="space-y-0.5 sm:space-y-1">
                      {dayTasks.slice(0, view === 'month' ? 3 : 20).map((t) => {
                        const overdue = t.status !== 'done' && isBefore(parseISO(t.due_date), startOfDay(new Date()));
                        return (
                          <div
                            key={t.id + (t.kind || '')}
                            data-testid="cal-task"
                            draggable={t.kind !== 'followup'}
                            onDragStart={() => setDrag(t)}
                            onDragEnd={() => setDrag(null)}
                            onClick={(e) => { e.stopPropagation(); if (t.kind !== 'followup') setOpenTaskId(t.id); }}
                            title={t.title + (t.assignee_id ? ` · ${userLookup[t.assignee_id]?.name}` : '')}
                            className={`px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] flex items-center gap-1 cursor-pointer truncate text-white ${PRI_COLOR[t.priority] || PRI_COLOR.medium} ${t.status === 'done' ? 'opacity-50 line-through' : ''} ${overdue ? 'ring-1 ring-red-700' : ''} ${t.kind === 'followup' ? 'italic' : ''}`}
                          >
                            {t.status === 'done' && <CheckCircle2 className="w-3 h-3 flex-shrink-0" />}
                            {overdue && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                            <span className="truncate">{t.title}</span>
                          </div>
                        );
                      })}
                      {view === 'month' && dayTasks.length > 3 && (
                        <div className="text-[10px] text-[#0b1a2e]/50">+{dayTasks.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <p className="mt-3 text-xs text-[#0b1a2e]/50 hidden sm:block">
        Tip: drag any task pill onto a different day to reschedule it. Click a task to open the full workspace (comments, files, approvals). Task's due-time defaults to 09:00 on the new day.
      </p>

      {openTaskId && (
        <TaskDetailDialog
          taskId={openTaskId}
          onClose={() => { setOpenTaskId(null); if (searchParams.get('task')) { searchParams.delete('task'); setSearchParams(searchParams); } }}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default CalendarPage;
